/**
 * Ad tag templates — the one place to edit the tags the app hands out for ad
 * units, both on the ad unit page and in the list's "Download tags" action.
 *
 * To add a tag type, add an entry to `adTagTemplates`. To add a VAST version,
 * add an entry to `vastVersions`. Nothing else needs to change.
 *
 * ── Placeholders ────────────────────────────────────────────────────────────
 *
 * In `template` (the whole tag):
 *   {{networkTagId}}  the network's tag ID
 *   {{env}}           `env: 'dev',` outside production, otherwise empty
 *   {{gdpr}}          `gdpr: 1,` / `gdpr: 0,` when chosen, otherwise empty
 *   {{targetDivs}}    `targetDiv` rendered for every ad unit in the request
 *   {{adUnits}}       `adUnit` rendered for every ad unit, comma separated
 *
 * In `targetDiv` and `adUnit` (repeated per ad unit), and in `vastTemplate`:
 *   {{auTagId}}       the ad unit's tag ID
 *   {{auName}}        the ad unit's name
 *   {{siteName}}      the ad unit's site name, or empty
 *
 * In `vastTemplate` only:
 *   {{deliveryHost}}  the ad server matching the API environment
 *   {{vastVersion}}   the chosen version's `id`, e.g. `vast4-1`
 *   {{gdprParam}}     `gdpr=1&` / `gdpr=0&` when chosen, otherwise empty
 *
 * Layout rules, so templates stay readable:
 *   - A line holding only a placeholder that renders empty is dropped.
 *   - A placeholder that renders several lines indents each of them like the
 *     line it sits on.
 */

export interface AdTagTemplate {
  /** Unique key used for selection state */
  id: string
  /** Display label shown in the dropdown */
  label: string
  /** Short description shown below the dropdown */
  description: string
  /** The whole tag. Use 2-space indentation. */
  template: string
  /** One ad unit's entry in `adUnits`. Defaults to `defaultAdUnit`. */
  adUnit?: string
  /** One ad unit's target div. Defaults to `defaultTargetDiv`. */
  targetDiv?: string
}

export const defaultTargetDiv = `<!-- {{auName}} -->
<div id="adn-{{auTagId}}" style="display:none"></div>`

export const defaultAdUnit = `{auId: '{{auTagId}}', auW: 0, auH: 0}`

/** The adn.js loader and request shared by the templates below. */
const requestTag = (extraParams = '') => `{{targetDivs}}
<script src="https://cdn.adnuntius.com/adn.js" async></script>
<script>
  window.adn = window.adn || {};
  adn.calls = adn.calls || [];
  adn.calls.push(function() {
    adn.request({
      {{env}}
      network: '{{networkTagId}}',
      {{gdpr}}
      {{requestParams}}
      {{refresh}}${extraParams}
      adUnits: [
        {{adUnits}}
      ]
    });
  });
</script>`

export const adTagTemplates: AdTagTemplate[] = [
  {
    id: 'standard',
    label: 'Standard',
    description: 'Standard ad tag with the adn.js script loader.',
    template: requestTag(),
  },
  {
    id: 'no-cookies',
    label: 'No cookies',
    description: 'Tag that disables cookies and user matching.',
    template: requestTag(`
      noCookies: true,`),
  },
  {
    id: 'segment',
    label: 'Segment targeting',
    description: 'Tag with segment targeting parameter included.',
    template: requestTag(`
      segments: ['segment-id-here'],`),
  },
  {
    id: 'key-value',
    label: 'Key-value targeting',
    description: 'Tag with key-value targeting parameters.',
    template: requestTag(),
    adUnit: `{auId: '{{auTagId}}', auW: 0, auH: 0, kv: {'key': ['value']}}`,
  },
]

/** The VAST URL a video player requests for one ad unit. */
export const vastTemplate =
  '{{deliveryHost}}/i?{{gdprParam}}network={{networkTagId}}&auId={{auTagId}}&tt={{vastVersion}}'

export const vastVersions = [
  { id: 'vast2', label: 'VAST 2.0' },
  { id: 'vast3', label: 'VAST 3.0' },
  { id: 'vast4', label: 'VAST 4.0' },
  { id: 'vast4-1', label: 'VAST 4.1' },
  { id: 'vast4-2', label: 'VAST 4.2' },
]
